import { useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useUserVerifyPersonalInfo } from '@repo/services'
import { useRouter } from '@repo/shared/i18n/navigation'
import { DAYS, MONTHS } from '@repo/shared/variables/validations'
import { handleNumericInput, handlePasteInput } from '@repo/tools'
import {
  Button,
  Form,
  FormField,
  FormItemWrapper,
  FormMessage,
  Input,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@repo/ui/components'
import { twMerge } from 'tailwind-merge'
import { useKycSchema } from '../../schemas'
import { IKycFormProps } from './kyc.types'

export const KycForm = (props: IKycFormProps) => {
  const { mobileOrEmail } = props
  const { t } = useTranslate()
  const { schema } = useKycSchema()
  const { form } = useAppForm({
    formSchema: schema,
  })
  const { replace } = useRouter()
  const verifyPersonalInfoMutation = useUserVerifyPersonalInfo({
    invalidate: true,
  })

  const onSubmit = async (data: FormSchema<typeof schema>) => {
    try {
      // First verify personal info with API
      const birthdate = {
        year: parseInt(data.year),
        month: parseInt(data.month),
        day: parseInt(data.day),
      }
      const verifyData = {
        nationalID: data.nationalCode,
        birthdate: birthdate,
      }

      const result = await verifyPersonalInfoMutation.mutateAsync(verifyData)

      if (result.data?.verified && !result.data?.userExists) {
        // If verification successful, proceed to selfie KYC
        const params = new URLSearchParams({
          ...data,
          mobileOrEmail,
          firstName: result.data?.firstName || '',
          lastName: result.data?.lastName || '',
        })
        replace(`/auth/selfie-kyc?${params.toString()}`)
      } else if (result.data?.userExists) {
        toast.error({
          title: t('error_USER_ALREADY_EXISTS'),
        })
      } else {
        // Handle verification failure
        toast.error({
          title: t('common_invalid_personal_info'),
        })
        // You can add error handling here, like showing a toast or error message
      }
    } catch (error) {
      console.error('Error during personal info verification:', error)
      // Handle error - you can add error handling here
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='flex flex-col gap-3xl w-full'
      >
        {/* <FormField
          control={form.control}
          name='firstName'
          render={({ field }) => (
            <FormItemWrapper
              title={tRich.rich('kyc_firstName', {
                option: (text) => (
                  <Typography weight='regular'>{text}</Typography>
                ),
              })}
              inputSize='lg'
            >
              <Input
                autoComplete='off'
                inputSize='lg'
                variant='fill'
                {...field}
                onKeyDown={(e) => handleNumericInput(e, 20, /[ا-یء-ي ]/)}
                onPaste={(e) => handlePasteInput(e, 20, /[ا-یء-ي ]/)}
              />
            </FormItemWrapper>
          )}
        />
        <FormField
          control={form.control}
          name='lastName'
          render={({ field }) => (
            <FormItemWrapper
              title={tRich.rich('kyc_lastName', {
                option: (text) => (
                  <Typography weight='regular'>{text}</Typography>
                ),
              })}
              inputSize='lg'
            >
              <Input
                autoComplete='off'
                inputSize='lg'
                variant='fill'
                {...field}
                onKeyDown={(e) => handleNumericInput(e, 20, /[ا-یء-ي ]/)}
                onPaste={(e) => handlePasteInput(e, 20, /[ا-یء-ي ]/)}
              />
            </FormItemWrapper>
          )}
        /> */}
        <Row className='gap-xs items-end'>
          <FormField
            control={form.control}
            name='day'
            displayValidation={false}
            render={({ field }) => (
              <Select {...field} onValueChange={field.onChange}>
                <FormItemWrapper title={t('kyc_birthDate')} inputSize='lg'>
                  <SelectTrigger inputSize='lg' className='!m-0'>
                    <SelectValue placeholder={t('common_day')} />
                  </SelectTrigger>
                </FormItemWrapper>
                <SelectContent>
                  {DAYS.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <FormField
            control={form.control}
            name='month'
            displayValidation={false}
            render={({ field }) => (
              <Select {...field} onValueChange={field.onChange}>
                <FormItemWrapper inputSize='lg'>
                  <SelectTrigger inputSize='lg' className='!m-0'>
                    <SelectValue placeholder={t('common_month')} />
                  </SelectTrigger>
                </FormItemWrapper>
                <SelectContent>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <FormField
            control={form.control}
            name='year'
            displayValidation={false}
            render={({ field }) => (
              <FormItemWrapper inputSize='lg'>
                <Input
                  placeholder={t('common_year')}
                  autoComplete='off'
                  type='text'
                  inputSize='lg'
                  variant='fill'
                  className={twMerge('w-full', field.value && 'ltr')}
                  {...field}
                  onKeyDown={(e) => handleNumericInput(e, 4)}
                  onPaste={(e) => handlePasteInput(e, 4)}
                  inputMode='numeric'
                />
              </FormItemWrapper>
            )}
          />
        </Row>

        {(form?.formState?.errors?.year?.message ||
          form?.formState?.errors?.month?.message ||
          form?.formState?.errors?.day?.message) && (
          <FormMessage
            color='text-negative-surface-100'
            size='sm'
            className='!-mt-2xl'
          >
            {String(
              form?.formState?.errors?.year?.message ??
                form?.formState?.errors?.month?.message ??
                form?.formState?.errors?.day?.message ??
                '',
            ) || '\u00A0'}
          </FormMessage>
        )}
        <FormField
          control={form.control}
          name='nationalCode'
          render={({ field }) => (
            <FormItemWrapper
              title={t('kyc_nationalCode')}
              caption={t('kyc_nationalCode_hint')}
              inputSize='lg'
            >
              <Input
                autoComplete='off'
                type='text'
                inputSize='lg'
                variant='fill'
                className='ltr'
                {...field}
                onKeyDown={(e) => handleNumericInput(e, 10)}
                onPaste={(e) => handlePasteInput(e, 10)}
                inputMode='numeric'
              />
            </FormItemWrapper>
          )}
        />
        <Button
          type='submit'
          className='w-full'
          size='lg'
          // loading={verifyPersonalInfoMutation.isPending}
          disabled={verifyPersonalInfoMutation.isPending}
        >
          {t(
            verifyPersonalInfoMutation.isPending ? 'kyc_pending' : 'kyc_button',
          )}
        </Button>
      </form>
    </Form>
  )
}
